/**
 * SyncRow — Tappable refresh row showing last sync time
 *
 * Sits below the Accounts card on dashboard. Tap to trigger sync.
 * Refresh icon spins during sync. Text updates to "Just now" on completion.
 */

import { useState, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { T } from '@/styles/tokens';

interface SyncRowProps {
  /** Unix ms of last successful sync. Falls back to "Never" if null. */
  lastSyncAt: number | null;
  /** Called when user taps to refresh. Should return a promise. */
  onSync: () => Promise<void>;
}

function formatElapsed(unixMs: number | null): string {
  if (!unixMs) return 'Never';
  const diffMs = Date.now() - unixMs;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  return `${Math.floor(diffHrs / 24)}d ago`;
}

export function SyncRow({ lastSyncAt, onSync }: SyncRowProps) {
  const [syncing, setSyncing] = useState(false);
  const [localLastSync, setLocalLastSync] = useState<number | null>(null);

  const effectiveLastSync = localLastSync ?? lastSyncAt;

  const handleSync = useCallback(async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      await onSync();
      setLocalLastSync(Date.now());
    } catch (err) {
      console.error('[SyncRow] Sync failed:', err);
    } finally {
      setSyncing(false);
    }
  }, [syncing, onSync]);

  return (
    <div
      onClick={handleSync}
      style={{
        padding: '8px 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        cursor: 'pointer',
      }}
    >
      <RefreshCw
        size={10}
        strokeWidth={2.5}
        color={T.primary}
        style={syncing ? { animation: 'spin 800ms linear infinite' } : undefined}
      />
      <span style={{ fontSize: 10, fontWeight: 600, color: T.primary }}>
        {syncing ? 'Syncing...' : `Synced ${formatElapsed(effectiveLastSync)}`}
      </span>
    </div>
  );
}
